import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Flex, Tab, TabList, TabPanel, TabPanels, Tabs, Text, Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { UserInfo } from './UserInfo/UserInfo'
import { UserVerification } from './UserVerification/UserVerification'
import { UserEvents } from './UserEvents/UserEvents'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as eva from 'eva-icons'
import { Link, useParams } from 'react-router-dom'
import './UserDetailPage.css'
import { createUsersApi } from './../../../utils/api-interface'
import { UserDetailsResponse, UserStatus } from '../../../services/network'
import { log } from '../../../utils/logger'

function UserDetailPage(): JSX.Element {
  const { t } = useTranslation()
  const { id } = useParams()
  const [user, setUserProfile] = useState<UserDetailsResponse>()
  const [selectedTab, setSelectedTab] = useState(0)
  const [loading, isLoading] = useState<boolean>(false)

  useEffect(() => {
    eva.replace()
  })

  const fetchProfile = useCallback(async () => {
    if (!id || loading) {
      log.error(`[UserDetailPage] id: ${id} - loading: ${loading}`)
      return
    }
    isLoading(true)
    try {
      const usersApi = createUsersApi()
      const response = await usersApi.getUser(id)
      setUserProfile(response.data)
    } catch (error) {
      log.error('Error fetching dashboard profile:', error)
    }
    isLoading(false)
  }, [id, loading])

  useEffect(() => {
    fetchProfile()
  }, [])

  const onUserUpdateHandler = useCallback(() => {
    fetchProfile()
  }, [fetchProfile])

  const isUserDeleted = useMemo((): boolean => {
    if (!user) {
      return true
    }
    if (user.status === UserStatus.Deleted) {
      return true
    }
    if (user.status === UserStatus.Banned) {
      if (user.id === user.email) {
        return true
      }
    }
    return false
  }, [user])

  return (
    <Flex flexDirection='column' alignItems={'left'} padding={'40px 20px'} gap={'30px'} height={'100vh'} minH='100vh'>
      <Breadcrumb spacing='8px' separator={<i data-eva={'chevron-right-outline'} data-eva-fill={'#353F48'}></i>}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to='/users'>
            <Text fontSize='16px' fontWeight='400' lineHeight='22.4px'>
              {t('users.title')}
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage={true}>
          <BreadcrumbLink href='#'>
            <Text fontSize='16px' fontWeight='700' lineHeight='24px'>
              {user?.firstName} {user?.lastName}
            </Text>
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Text fontSize='26px' fontWeight='700' lineHeight='31.2px' textAlign='left'>
        {t('user_tab')}
      </Text>
      <Tabs variant={'primary'} overflow={'auto'}>
        <TabList>
          <Tab onClick={(): void => setSelectedTab(0)} className={selectedTab === 0 ? 'userpage-tab-selected' : 'userpage-tab'}>
            <Flex direction={'column'} alignItems={'center'} gap={'4px'}>
              <i data-eva={'edit-2-outline'} className='eva-icon'></i>
              <Text fontSize='14px' fontWeight='700' lineHeight='16px'>
                {t('info')}
              </Text>
            </Flex>
          </Tab>
          <Tab onClick={(): void => setSelectedTab(1)} className={selectedTab === 1 ? 'userpage-tab-selected' : 'userpage-tab'}>
            <Flex direction={'column'} alignItems={'center'} gap={'4px'} key={'tab1'}>
              <i data-eva={'shield-outline'} className='eva-icon'></i>
              <Text fontSize='14px' fontWeight='700' lineHeight='16px'>
                {t('user_data')}
              </Text>
            </Flex>
          </Tab>
          <Tab onClick={(): void => setSelectedTab(2)} className={selectedTab === 2 ? 'userpage-tab-selected' : 'userpage-tab'}>
            <Flex direction={'column'} alignItems={'center'} gap={'4px'}>
              <i data-eva={'calendar-outline'} className='eva-icon'></i>
              <Text fontSize='14px' fontWeight='700' lineHeight='16px'>
                {t('event')}
              </Text>
            </Flex>
          </Tab>
        </TabList>
        <TabPanels>
          <TabPanel>
            <UserInfo onUserUpdate={onUserUpdateHandler} isUserDeleted={isUserDeleted} user={user} />
          </TabPanel>
          <TabPanel>
            <UserVerification onUserUpdate={onUserUpdateHandler} isUserDeleted={isUserDeleted} user={user} />
          </TabPanel>
          <TabPanel>
            <UserEvents />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  )
}

export { UserDetailPage }
